// Lógica PURA de «qué trae Escalafy» en la pantalla de economía: la franja del formulario, qué campos
// llegaron solos y cuándo fue la última actualización.
//
// Sin React ni i18n (react-router v7 no carga en Jest). Lo que lee viene del backend, en el bloque
// `escalafy` de `GET client/<id>/economics/` y en cada conexión de la lista:
// - `last_sync_at`: el último dato BUENO (un intento fallido no lo mueve).
// - `last_sync_attempt_at`: cuándo terminó el último intento, bien o mal.
// - `sync_in_progress`: hay un pedido («Actualizar ahora» o el vínculo) que todavía no terminó.
// - `derivation`: la última derivación resumida — `fields` (qué datos del perfil derivó), `warnings`
//   (por qué no derivó el resto), `window` (cuántos días con ventas) y `outcome`.
// Con un backend que todavía no los manda, todo degrada a «no se marca nada».

import {daysSince} from '../../panel_v11/panelFormatters';
import {FORM_FIELDS, formatInputNumber, parseLocaleNumber} from './economiaModel';

export const FIELD_SYNCED = 'synced';
export const FIELD_EDITED = 'edited';
export const FIELD_KEPT = 'kept';
export const FIELD_MISSING = 'missing';

export const STRIP_SYNCING = 'syncing';
export const STRIP_OK = 'ok';
export const STRIP_WARN = 'warn';
export const STRIP_MANUAL = 'manual';
export const STRIP_UNUSED = 'unused';
export const STRIP_WAITING = 'waiting';

const ESCALAFY_SOURCE = 'ESCALAFY';
// Las cargas humanas: una posterior al vínculo GANA y Escalafy deja de pisar el perfil
// (`EscalafySyncService._manual_version_wins`).
const MANUAL_SOURCES = new Set(['MANUAL', 'CSV']);
// Lo que el sync deja en `outcome` cuando derivó pero no creó versión porque no alcanzaba para un perfil.
const UNUSED_OUTCOMES = new Set(['NO_GROSS_MARGIN', 'INVALID_FOR_PROFILE']);

// Por qué Escalafy no trajo un campo: el primer aviso de la derivación que lo explica, del más general
// al más específico. Son los `WARNING_*` de `escalafy/profile_deriver.py`. El ticket no está: sin el de
// Escalafy lo calcula Dashbo de la tienda, y el campo ya lo dice.
const MISSING_REASONS = {
    gross_margin_pct: ['NO_SALES', 'COGS_HIDDEN', 'NO_PRODUCT_COSTS', 'GROSS_MARGIN_OUT_OF_RANGE'],
    shipping_per_order: ['NO_SALES', 'SHIPPING_NOT_AVAILABLE', 'NO_ORDERS'],
    payment_fee_pct: ['NO_SALES', 'NET_REVENUE_HIDDEN', 'COGS_HIDDEN', 'SHIPPING_NOT_AVAILABLE',
        'PAYMENT_FEE_OUT_OF_RANGE'],
    fixed_costs_monthly: ['COSTS_NOT_AVAILABLE', 'NO_FIXED_COSTS', 'COSTS_IN_OTHER_CURRENCY'],
    agency_fee_monthly: ['COSTS_NOT_AVAILABLE', 'AGENCY_FEE_NOT_FOUND'],
};

function linked(escalafy) {
    return !!(escalafy && escalafy.connected === true);
}

function derivedFields(escalafy) {
    const fields = escalafy && escalafy.derivation && escalafy.derivation.fields;
    return Array.isArray(fields) ? fields.filter((field) => FORM_FIELDS.includes(field)) : [];
}

function hasValue(value) {
    return value !== null && value !== undefined && value !== '';
}

// El valor del perfil tal como lo muestra el input (2 decimales): comparar contra el crudo marcaría
// «Editado» un campo que nadie tocó.
function sameAsProfile(typed, profileValue) {
    const shown = parseLocaleNumber(formatInputNumber(profileValue));
    const current = parseLocaleNumber(typed);
    return Number.isFinite(shown) && Number.isFinite(current) && Math.abs(shown - current) < 0.005;
}

/**
 * De dónde sale cada campo del formulario, sólo cuando el perfil vigente lo creó Escalafy:
 *
 * - `synced`: lo derivó Escalafy y está como vino.
 * - `edited`: lo derivó Escalafy y el usuario lo está cambiando (`value` = el de Escalafy).
 * - `kept`: Escalafy no lo informa hoy y el perfil conserva el valor anterior.
 * - `missing`: Escalafy no lo informa y está vacío; `reason` es el aviso que lo explica (o `null`).
 *
 * Con un perfil manual (gana sobre Escalafy) o sin derivación, `{}`: ningún campo se marca.
 */
export function escalafyFieldSources(escalafy, profile, values) {
    if (!linked(escalafy) || !escalafy.derivation || !profile || profile.source !== ESCALAFY_SOURCE) return {};
    const derived = new Set(derivedFields(escalafy));
    const warnings = Array.isArray(escalafy.derivation.warnings) ? escalafy.derivation.warnings : [];
    const sources = {};
    FORM_FIELDS.forEach((field) => {
        const profileValue = profile[field];
        if (derived.has(field) && hasValue(profileValue)) {
            sources[field] = sameAsProfile((values || {})[field], profileValue)
                ? {kind: FIELD_SYNCED}
                : {kind: FIELD_EDITED, value: profileValue};
            return;
        }
        if (hasValue(profileValue)) {
            sources[field] = {kind: FIELD_KEPT};
            return;
        }
        if (!MISSING_REASONS[field]) return;
        const reason = MISSING_REASONS[field].find((code) => warnings.includes(code)) || null;
        sources[field] = {kind: FIELD_MISSING, reason};
    });
    return sources;
}

/**
 * La franja de arriba del formulario (`null` = sin Escalafy vinculado):
 *
 * - `syncing`: hay una actualización en curso (la que pidió esta pantalla o una que el backend sabe).
 * - `warn`: la conexión no está OK (plan, rol, desconectada); `lastSyncAt` = el último dato bueno.
 * - `manual`: una carga a mano posterior le ganó a Escalafy, que ahora sólo propone.
 * - `unused`: Escalafy trajo datos que no alcanzan para un perfil (sin margen); `reason` = el aviso, y
 *   `keeps` si el perfil vigente es de un Escalafy anterior (que sigue: la franja no cuenta los datos de
 *   una derivación que no se aplicó, R-escalafy-04).
 * - `waiting`: vinculado y sin ningún dato todavía.
 * - `ok`: el perfil vigente es de Escalafy; `count` de los seis datos y `days` con ventas.
 */
export function escalafyStripState(escalafy, profile, {syncing = false} = {}) {
    if (!linked(escalafy)) return null;
    if (syncing || escalafy.sync_in_progress === true) return {kind: STRIP_SYNCING};
    const status = escalafy.reconnect_required ? 'DISCONNECTED' : (escalafy.status || 'OK');
    if (status !== 'OK') return {kind: STRIP_WARN, status, lastSyncAt: escalafy.last_sync_at || null};
    const source = profile && profile.source;
    const derivation = escalafy.derivation || null;
    if (MANUAL_SOURCES.has(source) && (escalafy.proposal || (derivation && derivation.outcome === 'SUPERSEDED_BY_MANUAL'))) {
        return {kind: STRIP_MANUAL};
    }
    if (source === ESCALAFY_SOURCE && derivation && UNUSED_OUTCOMES.has(derivation.outcome)) {
        return {kind: STRIP_UNUSED, reason: unusedReason(derivation), keeps: true};
    }
    if (source === ESCALAFY_SOURCE) {
        const window = (derivation && derivation.window) || {};
        return {
            kind: STRIP_OK,
            count: derivation ? derivedFields(escalafy).length : null,
            total: FORM_FIELDS.length,
            days: Number.isFinite(Number(window.days)) && Number(window.days) > 0 ? Number(window.days) : null,
            lastSyncAt: escalafy.last_sync_at || null,
        };
    }
    if (derivation && UNUSED_OUTCOMES.has(derivation.outcome)) {
        return {kind: STRIP_UNUSED, reason: unusedReason(derivation), keeps: false};
    }
    if (!escalafy.last_sync_at) return {kind: STRIP_WAITING};
    return null;
}

function unusedReason(derivation) {
    const warnings = Array.isArray(derivation.warnings) ? derivation.warnings : [];
    return MISSING_REASONS.gross_margin_pct.find((code) => warnings.includes(code)) || null;
}

/**
 * Cuándo, en el formato de una fila de conexión: `{key: 'today'|'yesterday'|'date', time, date}`.
 * La hora va en la zona del navegador (la del usuario). `null` si no hay fecha.
 */
export function syncMoment(isoDate, language, now = new Date()) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return null;
    const locale = String(language || '').startsWith('en') ? 'en-US' : 'es-AR';
    const time = date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit', hour12: false});
    const days = daysSince(date, now);
    if (days === 0) return {key: 'today', time};
    if (days === 1) return {key: 'yesterday', time};
    const options = {day: 'numeric', month: 'short'};
    if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return {key: 'date', time, date: date.toLocaleDateString(locale, options)};
}

/**
 * La marca con la que la pantalla sabe que un intento TERMINÓ: el fin del último intento, bien o mal.
 * Con un backend viejo (sin `last_sync_attempt_at`), el último dato bueno — un fallo no lo mueve, y la
 * espera se corta sola a los dos minutos.
 */
export function syncStamp(connection) {
    if (!connection) return null;
    if (connection.last_sync_attempt_at !== undefined) return connection.last_sync_attempt_at || null;
    return connection.last_sync_at || null;
}

export function syncStamps(connections) {
    const stamps = {};
    (connections || []).forEach((connection) => { stamps[connection.id] = syncStamp(connection); });
    return stamps;
}

/**
 * Las conexiones cuyo intento terminó desde `stamps`. Con `ids`, sólo las pedidas: con varias conexiones,
 * la espera de «Actualizar ahora» en una no termina (ni avisa) por lo que pasó en otra (R-escalafy-05).
 */
export function finishedConnections(connections, stamps, ids = null) {
    const wanted = ids ? new Set(ids.map(String)) : null;
    return (connections || []).filter((connection) => {
        if (wanted && !wanted.has(String(connection.id))) return false;
        const stamp = syncStamp(connection);
        return !!stamp && stamp !== stamps[connection.id];
    });
}

export function syncedSince(connections, stamps, ids = null) {
    return finishedConnections(connections, stamps, ids).length > 0;
}

/**
 * ¿El último intento terminó sin traer datos? Un fallo transitorio (cupo, red, sin datos) deja el estado en
 * OK y no mueve el último dato bueno: la única marca es que el intento es posterior al dato (R-escalafy-03).
 */
export function lastAttemptFailed(connection) {
    if (!connection) return false;
    const attempt = connection.last_sync_attempt_at;
    return !!attempt && attempt !== connection.last_sync_at;
}

/** ¿Alguna conexión de la lista sigue en curso según el backend? (el tope de 10 min lo pone él) */
export function anyInProgress(connections) {
    return (connections || []).some((connection) => connection && connection.sync_in_progress === true);
}
